import { useMemo, useState } from 'react';
import { ErrorState, Loading } from '../../components/States';
import { useAdminBankTransactions, useAdminOrders, useReviewAdminBankTransaction } from '../../hooks/api-hooks';
import { formatDateTime, formatPrice } from '../../lib/format';
import type { AdminBankTransaction, AdminOrder } from '../../lib/types';

const orderStatus: Record<string, string> = {
  PENDING: 'Chờ thanh toán', PAID: 'Đã thanh toán', EXPIRED: 'Hết hạn',
  CANCELLED: 'Đã hủy', REVIEW_REQUIRED: 'Cần kiểm tra',
};

function ReviewTransactionRow({ transaction, orders }: { transaction: AdminBankTransaction; orders: AdminOrder[] }) {
  const review = useReviewAdminBankTransaction();
  const [orderId, setOrderId] = useState(transaction.orderId ?? '');
  const [note, setNote] = useState('');
  const candidates = useMemo(
    () => orders.filter((order) => ['PENDING', 'REVIEW_REQUIRED', 'EXPIRED'].includes(order.status)),
    [orders],
  );
  const chosen = candidates.find((order) => order.id === orderId);

  function match() {
    if (!chosen) return;
    const mismatch = Number(chosen.total) !== Number(transaction.transferAmount);
    if (mismatch && !window.confirm('Số tiền giao dịch khác tổng đơn hàng. Anh vẫn muốn xác nhận thủ công?')) return;
    review.mutate({ id: transaction.id, input: { action: 'MATCH_ORDER', orderId, note } });
  }

  return (
    <tr>
      <td><strong>{transaction.gateway || 'Ngân hàng'}</strong><small>{transaction.providerTransactionId}</small></td>
      <td>{formatPrice(Number(transaction.transferAmount))}<small>{transaction.content || transaction.code || 'Không có nội dung'}</small></td>
      <td>{formatDateTime(transaction.transactionAt || transaction.createdAt)}</td>
      <td><span className={`status-badge ${transaction.reconciliationStatus === 'MATCHED' ? 'published' : ''}`}>{transaction.reconciliationStatus}</span><small>{transaction.reconciliationReason}</small></td>
      <td>
        {transaction.reconciliationStatus === 'MATCHED' ? (
          <strong>{transaction.orderCode}</strong>
        ) : (
          <div className="admin-inline-review">
            <select value={orderId} onChange={(event) => setOrderId(event.target.value)}>
              <option value="">Chọn đơn hàng…</option>
              {candidates.map((order) => <option key={order.id} value={order.id}>{order.orderCode} · {formatPrice(Number(order.total))}</option>)}
            </select>
            <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Ghi chú kiểm tra" maxLength={500} />
            <div>
              <button type="button" className="btn btn-small" disabled={!orderId || review.isPending} onClick={match}>Khớp đơn</button>
              <button type="button" className="btn btn-small btn-quiet" disabled={review.isPending} onClick={() => review.mutate({ id: transaction.id, input: { action: 'IGNORE', note } })}>Bỏ qua</button>
            </div>
            {review.error && <span className="error">{review.error.message}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}

export function AdminOrdersPage() {
  const [tab, setTab] = useState<'ORDERS' | 'TRANSACTIONS'>('ORDERS');
  const orders = useAdminOrders();
  const transactions = useAdminBankTransactions();

  return (
    <section className="admin-page">
      <header className="admin-page-header"><div><h1>Đơn hàng & giao dịch</h1><p>Đối chiếu thanh toán và xử lý các giao dịch SePay chưa tự khớp.</p></div></header>
      <div className="admin-tabs" role="tablist">
        <button type="button" className={tab === 'ORDERS' ? 'active' : ''} onClick={() => setTab('ORDERS')}>Đơn hàng</button>
        <button type="button" className={tab === 'TRANSACTIONS' ? 'active' : ''} onClick={() => setTab('TRANSACTIONS')}>Giao dịch ngân hàng</button>
      </div>
      {tab === 'ORDERS' && <>
        {orders.isPending && <Loading label="Đang tải đơn hàng…" />}
        {orders.error && <ErrorState error={orders.error} />}
        {orders.data && <div className="admin-table-wrap"><table className="admin-table">
          <thead><tr><th>Đơn hàng</th><th>Học viên</th><th>Sản phẩm</th><th>Tổng tiền</th><th>Ngày tạo</th><th>Trạng thái</th></tr></thead>
          <tbody>{orders.data.map((order) => <tr key={order.id}>
            <td><strong>{order.orderCode}</strong><small>{order.gateway || 'Chưa có thanh toán'}</small></td>
            <td><strong>{order.fullName || 'Chưa đặt tên'}</strong><small>{order.email}</small></td>
            <td><span className="admin-tags">{order.items.map((item) => <small key={item.id}>{item.title}</small>)}</span></td>
            <td>{formatPrice(Number(order.total))}</td><td>{formatDateTime(order.createdAt)}</td>
            <td><span className={`status-badge ${order.status === 'PAID' ? 'published' : ''}`}>{orderStatus[order.status] || order.status}</span></td>
          </tr>)}{!orders.data.length && <tr><td colSpan={6} className="admin-empty">Chưa có đơn hàng.</td></tr>}</tbody>
        </table></div>}
      </>}
      {tab === 'TRANSACTIONS' && <>
        {transactions.isPending && <Loading label="Đang tải giao dịch…" />}
        {transactions.error && <ErrorState error={transactions.error} />}
        {transactions.data && orders.data && <div className="admin-table-wrap"><table className="admin-table admin-review-table">
          <thead><tr><th>Giao dịch</th><th>Số tiền</th><th>Thời gian</th><th>Đối soát</th><th>Xử lý thủ công</th></tr></thead>
          <tbody>{transactions.data.map((transaction) => <ReviewTransactionRow key={transaction.id} transaction={transaction} orders={orders.data} />)}{!transactions.data.length && <tr><td colSpan={5} className="admin-empty">Chưa có giao dịch ngân hàng.</td></tr>}</tbody>
        </table></div>}
      </>}
    </section>
  );
}
